/**
 * Admin password policy — keep in sync with backend admin-password.crypto.ts
 */
export const ADMIN_PASSWORD_MIN_LENGTH = 8

export const ADMIN_PASSWORD_SPECIAL_CHARS =
  "!@#$%^&*()-_+=[]{}|\\:;\"'<>,.?/`~"

export const ADMIN_PASSWORD_SPECIAL_CHARS_DISPLAY =
  "! @ # $ % ^ & * ( ) - _ + = [ ] { } | \\ : ; ' \" < > , . ? / ` ~"

export const ADMIN_PASSWORD_POLICY_MESSAGE =
  'Password must be at least 8 characters and contain at least one uppercase letter, one lowercase letter, one number, and one special character from the allowed set.'

export const ADMIN_PASSWORD_UNSUPPORTED_CHAR_MESSAGE =
  `Password contains an unsupported character. Use only letters, numbers, and these special characters: ${ADMIN_PASSWORD_SPECIAL_CHARS_DISPLAY}`

const SPECIAL_SET = new Set(ADMIN_PASSWORD_SPECIAL_CHARS.split(''))

export function evaluateAdminPasswordPolicy(password) {
  const value = password ?? ''
  let hasUppercase = false
  let hasLowercase = false
  let hasDigit = false
  let hasAllowedSpecial = false
  let onlyAllowedChars = true

  for (const ch of value) {
    if (ch >= 'A' && ch <= 'Z') {
      hasUppercase = true
      continue
    }
    if (ch >= 'a' && ch <= 'z') {
      hasLowercase = true
      continue
    }
    if (ch >= '0' && ch <= '9') {
      hasDigit = true
      continue
    }
    if (SPECIAL_SET.has(ch)) {
      hasAllowedSpecial = true
      continue
    }
    onlyAllowedChars = false
  }

  return {
    minLength: value.length >= ADMIN_PASSWORD_MIN_LENGTH,
    hasUppercase,
    hasLowercase,
    hasDigit,
    hasAllowedSpecial,
    onlyAllowedChars,
  }
}

export function isAdminPasswordValid(password) {
  const c = evaluateAdminPasswordPolicy(password)
  return (
    c.minLength &&
    c.hasUppercase &&
    c.hasLowercase &&
    c.hasDigit &&
    c.hasAllowedSpecial &&
    c.onlyAllowedChars
  )
}

export function getAdminPasswordPolicyError(password) {
  const c = evaluateAdminPasswordPolicy(password)
  if (!c.onlyAllowedChars) return ADMIN_PASSWORD_UNSUPPORTED_CHAR_MESSAGE
  if (
    !c.minLength ||
    !c.hasUppercase ||
    !c.hasLowercase ||
    !c.hasDigit ||
    !c.hasAllowedSpecial
  ) {
    return ADMIN_PASSWORD_POLICY_MESSAGE
  }
  return null
}

export const ADMIN_PASSWORD_REQUIREMENT_ITEMS = [
  { key: 'minLength', label: 'At least 8 characters' },
  { key: 'hasUppercase', label: 'One uppercase letter' },
  { key: 'hasLowercase', label: 'One lowercase letter' },
  { key: 'hasDigit', label: 'One number' },
  {
    key: 'hasAllowedSpecial',
    label: `One special character from: ${ADMIN_PASSWORD_SPECIAL_CHARS_DISPLAY}`,
  },
  { key: 'onlyAllowedChars', label: 'No unsupported characters' },
]
